"use client";

import React, { useCallback, useMemo } from "react";
import { Label } from "../../primitives/label";
import {
  UploadDropzone,
  UploadDropzoneProps
} from "../../upload/UploadDropzone/UploadDropzone";
import { FormControlWrapper } from "../FormControlWrapper";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { FormControlWrapperProps } from "../FormControlWrapper";
import { cn } from "@gettactic/helpers";

export interface DropZoneUploadInputProps
  extends Omit<
    UploadDropzoneProps,
    "value" | "onChange" | "allowMultiple" | "onUpload" | "onUploadComplete"
  > {
  /** The selected file */
  value?: File | null;
  /** Callback when file changes */
  onChange?: (file: File | null) => void;
  /** Existing image URL to show as preview when no file is selected */
  previewUrl?: string | null;
  /** Label for the input */
  label?: string;
  /** Error message */
  error?: string;
  /** Additional class name */
  className?: string;
}

export type DropZoneUploadInputPropsWithFormControl = DropZoneUploadInputProps &
  FormControlWrapperProps;

// Module-level cache to persist URLs across component remounts (e.g., tab switches)
const fileUrlCache = new WeakMap<File, string>();

const DropZoneUploadInputCore: React.FC<DropZoneUploadInputProps> = ({
  value,
  onChange,
  previewUrl: externalPreviewUrl,
  label,
  error,
  className,
  ...dropzoneProps
}) => {
  // Create preview URL for the file using WeakMap cache
  // This ensures URLs persist across tab switches but are cleaned up when File is garbage collected
  const dropzoneValue = useMemo(() => {
    // If we have a file, get or create a preview URL for it
    if (value) {
      let url = fileUrlCache.get(value);
      if (!url) {
        url = URL.createObjectURL(value);
        fileUrlCache.set(value, url);
      }
      return [{ id: value.name, url }];
    }

    // If we have an external preview URL (existing image), show it
    if (externalPreviewUrl) {
      return [{ id: "existing", url: externalPreviewUrl }];
    }

    return undefined;
  }, [value, externalPreviewUrl]);

  const handleChange = useCallback(
    (files: File[]) => {
      onChange?.(files[0] || null);
    },
    [onChange]
  );

  return (
    <div className={cn("w-full", className)}>
      {label && <Label className="mb-2 block">{label}</Label>}
      <UploadDropzone
        {...dropzoneProps}
        value={dropzoneValue}
        onChange={handleChange}
        allowMultiple={false}
      />
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export const DropZoneUploadInput: React.FC<
  DropZoneUploadInputPropsWithFormControl
> = (props) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <FormControlWrapper variant="unstyled" {...wrapperProps}>
      <DropZoneUploadInputCore {...inputProps} />
    </FormControlWrapper>
  );
};

DropZoneUploadInput.displayName = "DropZoneUploadInput";
